/**
 * 认证管理器
 * 处理登录、登出、token管理等功能
 */

import type {
  UserInfo,
  LoginCredentials,
  AuthResponse,
  AuthState,
  AuthConfig,
  AuthEventType,
  AuthEventData
} from './types'
import { permissionManager } from './permission-manager'
import { storage } from './storage'

/**
 * 认证事件回调类型
 */
type AuthEventCallback = (event: AuthEventData) => void

/**
 * 认证管理器类
 */
export class AuthManager {
  private config: AuthConfig | null = null
  private eventListeners: Map<AuthEventType, AuthEventCallback[]> = new Map()
  private refreshTimer: number | null = null
  private apiClient: any = null

  /**
   * 初始化认证管理器
   */
  initialize(config: AuthConfig, apiClient?: any): void {
    this.config = {
      tokenKey: 'auth_token',
      userKey: 'auth_user',
      permissionsKey: 'auth_permissions',
      rolesKey: 'auth_roles',
      refreshTokenKey: 'auth_refresh_token',
      tokenExpirationKey: 'auth_token_expiration',
      storage: 'localStorage',
      ...config
    }
    this.apiClient = apiClient

    // 自动恢复认证状态
    this.restoreAuthState()

    // 设置自动刷新token
    this.setupTokenRefresh()
  }

  /**
   * 用户登录
   */
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    try {
      this.emitEvent('login', {
        type: 'login',
        timestamp: new Date().toISOString(),
        metadata: { username: credentials.username }
      })

      // 调用登录API
      const response = await this.callApi('login', credentials)

      // 保存认证信息
      await this.setAuthData(response)

      // 初始化权限管理器
      permissionManager.initialize(this.getAuthState())

      return response
    } catch (error: any) {
      this.emitEvent('login', {
        type: 'login',
        timestamp: new Date().toISOString(),
        metadata: {
          username: credentials.username,
          error: error.message
        }
      })
      throw error
    }
  }

  /**
   * 用户登出
   */
  async logout(reason?: string): Promise<void> {
    try {
      const userId = this.getCurrentUser()?.id

      // 调用登出API
      if (this.apiClient) {
        await this.callApi('logout')
      }

      // 清理本地数据
      this.clearAuthData()

      // 清理权限管理器
      permissionManager.clear()

      // 清理定时器
      this.clearRefreshTimer()

      this.emitEvent('logout', {
        type: 'logout',
        userId,
        timestamp: new Date().toISOString(),
        metadata: { reason }
      })
    } catch (error: any) {
      console.error('登出失败:', error)
      // 即使API调用失败，也要清理本地数据
      this.clearAuthData()
      permissionManager.clear()
      this.clearRefreshTimer()
    }
  }

  /**
   * 刷新token
   */
  async refreshToken(): Promise<string | null> {
    try {
      const refreshToken = storage.getRefreshToken()
      if (!refreshToken) {
        throw new Error('没有刷新token')
      }

      const response = await this.callApi('refresh', { refreshToken })

      // 更新token
      storage.setToken(response.token)
      if (response.refreshToken) {
        storage.setRefreshToken(response.refreshToken)
      }

      this.emitEvent('token_refresh', {
        type: 'token_refresh',
        userId: this.getCurrentUser()?.id,
        timestamp: new Date().toISOString()
      })

      return response.token
    } catch (error: any) {
      console.error('刷新token失败:', error)
      // 刷新失败，清理认证状态
      await this.logout('token_refresh_failed')
      return null
    }
  }

  /**
   * 检查是否已认证
   */
  isAuthenticated(): boolean {
    const token = storage.getToken()
    if (!token) {
      return false
    }

    // 检查token是否过期
    if (this.isTokenExpired(token)) {
      return false
    }

    return true
  }

  /**
   * 获取当前用户
   */
  getCurrentUser(): UserInfo | null {
    return storage.getUser()
  }

  /**
   * 获取当前token
   */
  getCurrentToken(): string | null {
    return storage.getToken()
  }

  /**
   * 获取认证状态
   */
  getAuthState(): AuthState {
    const user = this.getCurrentUser()
    const token = this.getCurrentToken()
    const permissions = storage.getPermissions() || []
    const roles = storage.getRoles() || []

    return {
      isAuthenticated: this.isAuthenticated(),
      user,
      token,
      permissions,
      roles,
      loading: false,
      error: null
    }
  }

  /**
   * 添加事件监听器
   */
  addEventListener(event: AuthEventType, callback: AuthEventCallback): void {
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, [])
    }
    this.eventListeners.get(event)!.push(callback)
  }

  /**
   * 移除事件监听器
   */
  removeEventListener(event: AuthEventType, callback: AuthEventCallback): void {
    const listeners = this.eventListeners.get(event)
    if (listeners) {
      const index = listeners.indexOf(callback)
      if (index > -1) {
        listeners.splice(index, 1)
      }
    }
  }

  /**
   * 恢复认证状态
   */
  private restoreAuthState(): void {
    const token = storage.getToken()
    const user = storage.getUser()

    if (token && user && !this.isTokenExpired(token)) {
      // 获取完整的认证状态数据
      const permissions = storage.getPermissions() || []
      const roles = storage.getRoles() || []

      // 构建完整的认证状态
      const authState: AuthState = {
        isAuthenticated: true,
        user,
        token,
        permissions,
        roles,
        loading: false,
        error: null
      }

      // 初始化权限管理器
      permissionManager.initialize(authState)

      // 设置自动刷新
      this.setupTokenRefresh()

    } else {
      // 清理过期数据
      this.clearAuthData()
    }
  }

  /**
   * 设置认证数据
   */
  private async setAuthData(response: AuthResponse): Promise<void> {
    storage.setToken(response.token)
    storage.setUser(response.user)
    storage.setPermissions(response.permissions)
    storage.setRoles(response.roles)

    if (response.refreshToken) {
      storage.setRefreshToken(response.refreshToken)
    }

    if (response.expiresAt) {
      storage.setTokenExpiration(response.expiresAt)
    }
  }

  /**
   * 清理认证数据
   */
  private clearAuthData(): void {
    storage.clearAuth()
  }

  /**
   * 检查token是否过期
   */
  private isTokenExpired(token: string): boolean {
    try {
      // 简单的JWT过期检查
      const payload = JSON.parse(atob(token.split('.')[1]))
      const now = Math.floor(Date.now() / 1000)
      return payload.exp < now
    } catch (error) {
      // 如果无法解析token，认为已过期
      return true
    }
  }

  /**
   * 设置自动刷新token
   */
  private setupTokenRefresh(): void {
    this.clearRefreshTimer()

    const token = storage.getToken()
    if (!token) return

    try {
      const payload = JSON.parse(atob(token.split('.')[1]))
      const exp = payload.exp * 1000 // 转换为毫秒
      const now = Date.now()
      const refreshTime = exp - now - 5 * 60 * 1000 // 提前5分钟刷新

      if (refreshTime > 0) {
        this.refreshTimer = window.setTimeout(() => {
          this.refreshToken()
        }, refreshTime)
      }
    } catch (error) {
      console.error('设置token刷新失败:', error)
    }
  }

  /**
   * 清理刷新定时器
   */
  private clearRefreshTimer(): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer)
      this.refreshTimer = null
    }
  }

  /**
   * 调用API
   */
  private async callApi(endpoint: string, data?: any): Promise<any> {
    if (!this.apiClient) {
      throw new Error('API客户端未配置')
    }

    // 这里可以根据实际的API客户端实现调用逻辑
    // 示例实现：
    switch (endpoint) {
      case 'login':
        return this.apiClient.post('/auth/login', data)
      case 'logout':
        return this.apiClient.post('/auth/logout')
      case 'refresh':
        return this.apiClient.post('/auth/refresh', data)
      default:
        throw new Error(`未知的API端点: ${endpoint}`)
    }
  }

  /**
   * 触发事件
   */
  private emitEvent(type: AuthEventType, data: AuthEventData): void {
    const listeners = this.eventListeners.get(type)
    if (listeners) {
      listeners.forEach(callback => {
        try {
          callback(data)
        } catch (error) {
          console.error('事件回调执行失败:', error)
        }
      })
    }
  }
}

// 创建全局认证管理器实例
export const authManager = new AuthManager()
